//TODO:check translation
import React, { ChangeEvent, useEffect, useState } from 'react';
import { Controller, useFormContext } from 'react-hook-form';

import { useTranslation } from '@mint-lib/i18n';
import styled from '@mint-lib/styled';

import { Box, Icon, Input, RadioWithLabel, Tooltip } from '@myn/mui';

import {
  AUTOMATIC,
  getBudgetReallocationAmountOptions,
  MANUAL,
  MAX_PERCENTAGE,
  MIN_FIELD_MAX_PERCENTAGE,
  MIN_PERCENTAGE,
} from '../../../../constants/recosSettings.js';
import { BlockBox } from '../atoms/index.jsx';

type Props = {
  isEditable: boolean;
};

type BudgetTrackOptions = {
  budgetType: string;
  min?: number;
  max?: number;
};

const BudgetRelocationAmount: React.FC<Props> = ({ isEditable }) => {
  const { t } = useTranslation('@myn/planning');

  const budgetReallocationAmountOptions = getBudgetReallocationAmountOptions(t);

  const {
    getValues,
    setValue,
    control,
    resetField,
    formState: { errors },
    watch,
    trigger,
  } = useFormContext();

  const [budgetType, setBudgetType] = useState<string>(
    getValues('isBudgetLimited') ? MANUAL : AUTOMATIC,
  );

  const minInputValue = watch('budgetReallocationLimits.min');
  const maxInputValue = watch('budgetReallocationLimits.max');

  const handleChangeBudget = (event: ChangeEvent<HTMLInputElement>) => {
    const parsedValue = Number.parseInt(
      event.currentTarget.value.replaceAll(/\D/g, ''),
    );
    // for min and max field maximum values are different (99 for min, 100 for max)
    const maxVal =
      event.currentTarget.id === 'budgetReallocationLimits.min'
        ? MIN_FIELD_MAX_PERCENTAGE
        : MAX_PERCENTAGE;
    /**
     * we checking on 'isNaN' because we can set empty field.
     * Previous check wasn't working on such case (parsedValue && +parsedValue > maxVal)
     *  */
    if (!isNaN(parsedValue) && parsedValue > maxVal) {
      setValue(event.currentTarget.id, maxVal);
      return;
    }

    if (!isNaN(parsedValue) && parsedValue < MIN_PERCENTAGE) {
      setValue(event.currentTarget.id, MIN_PERCENTAGE);
      return;
    }

    if (isNaN(parsedValue)) {
      setValue(event.currentTarget.id, null);
      return;
    }

    setValue(event.target.id, parsedValue);
  };

  useEffect(() => {
    setValue('isBudgetLimited', budgetType === MANUAL);

    if (budgetType === AUTOMATIC) {
      resetField('budgetReallocationLimits.min', { defaultValue: null });
      resetField('budgetReallocationLimits.max', { defaultValue: null });
    }
  }, [budgetType]);

  useEffect(() => {
    if (budgetType === MANUAL && (minInputValue || maxInputValue)) {
      trigger(['budgetReallocationLimits.min', 'budgetReallocationLimits.max']);
    }
  }, [minInputValue, maxInputValue, budgetType, trigger]);

  useEffect(() => {
    const trackValue: BudgetTrackOptions = { budgetType };
    if (budgetType === MANUAL) {
      trackValue.min = minInputValue ?? undefined;
      trackValue.max = maxInputValue ?? undefined;
    }
  }, [budgetType, minInputValue, maxInputValue]);

  const inputsDisabled = !isEditable || budgetType !== MANUAL;

  const radioButtonsTooltips = [
    t('The reallocated budget will be between -50% and +100%'),
    <>
      <>{t('Customize reallocated budget')}</>
      <br />
      <>{t('within min and max value')}</>
    </>,
  ];

  return (
    <BlockBox
      title={t('Budget reallocation amount')}
      description={t('The value of the budget reallocation can be:')}
    >
      <Container>
        <div>
          {budgetReallocationAmountOptions.map((option, index) => (
            <Box display="flex" alignItems="center" key={option.value}>
              <RadioWithLabel
                key={option.value}
                data-testid={option.value}
                label={option.name}
                disabled={!isEditable}
                checked={option.value === budgetType}
                onChange={() => setBudgetType(option.value)}
              />
              <Tooltip
                title={radioButtonsTooltips[index]}
                placement="top"
                arrow
                disableHoverListener={!isEditable}
                componentsProps={{
                  tooltip: { sx: { maxWidth: '100vw', textWrap: 'nowrap' } },
                }}
              >
                <div>
                  <Icon
                    component="Information"
                    fontSize="small"
                    aria-label="info-icon"
                  />
                </div>
              </Tooltip>
            </Box>
          ))}
        </div>
        <div>
          <Controller
            control={control}
            name="budgetReallocationLimits.min"
            render={({ field: { value } }) => (
              <Box width="150px">
                <Input
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  size="small"
                  id="budgetReallocationLimits.min"
                  data-testid="budgetReallocationLimits.min"
                  placeholder="1 - 99"
                  value={value ?? ''}
                  fullWidth={true}
                  onChange={handleChangeBudget}
                  disabled={inputsDisabled}
                  // @ts-ignore
                  error={errors.budgetReallocationLimits?.min}
                  // @ts-ignore
                  helperText={errors.budgetReallocationLimits?.min?.message}
                  endAdornment="%"
                  label={`+ ${t('Min')}`}
                  infoText={t('Set value between 1% and 99%')}
                  TooltipProps={{
                    placement: 'top',
                    arrow: true,
                  }}
                />
              </Box>
            )}
          />
          <Controller
            control={control}
            name="budgetReallocationLimits.max"
            render={({ field: { value } }) => (
              <Box width="150px">
                <Input
                  type="text"
                  inputProps={{
                    inputMode: 'numeric',
                    pattern: '[0-9]*',
                  }}
                  size="small"
                  id="budgetReallocationLimits.max"
                  data-testid="budgetReallocationLimits.max"
                  placeholder="1 - 100"
                  value={value ?? ''}
                  fullWidth={true}
                  onChange={handleChangeBudget}
                  disabled={inputsDisabled}
                  // @ts-ignore
                  error={errors.budgetReallocationLimits?.max}
                  // @ts-ignore
                  helperText={errors.budgetReallocationLimits?.max?.message}
                  endAdornment="%"
                  label={`+ ${t('Max')}`}
                  infoText={t('Set value between 1% and 100%')}
                  TooltipProps={{
                    placement: 'left',
                    arrow: true,
                  }}
                />
              </Box>
            )}
          />
        </div>
      </Container>
    </BlockBox>
  );
};

const Container = styled('div')`
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    margin-bottom: ${({ theme }) => theme.spacing(1.5)};
    > div {
      display: flex;
      flex-basis: 220px;
      align-items: center;
        &:first-child {
          margin-top: ${({ theme }) => theme.spacing(3.125)};
          & > div: first-child {
            margin-right: 1.75rem;
          }
          label {
            margin-right: ${({ theme }) => theme.spacing(0.625)};
          }
        }
      &:last-child {
        align-items: flex-start;
          > div:first-child {
            margin: 0 ${({ theme }) => theme.spacing(1)} 0 0};
          }
      }

      svg {
        display: flex;
        align-self: center;
      }

      svg,
      path,
      rect,
      circle {
        fill: currentColor !important;
      }
    }
`;

export default BudgetRelocationAmount;
